import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getSuperAdminDashboardData, approvePayout, recalculateCommissionsForPeriod, updateCommission, getUsers, createUser, updateUser, deleteUser, uploadPoliciesExcel, createManualPolicy, getPolicies, deletePolicy, updatePolicy, processCommissionPayment, sendCommissionNotification, getCommissionSettings } from '../model/api.js';
import Card from './components/common/Card.jsx';
import Table from './components/common/Table.jsx';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import CommissionManager from './CommissionManager.jsx';
import { CalculationModal, PaymentModal, ApprovalModal } from './components/SuperAdminModals.jsx';
import { DashboardTab, PayoutsTab, PaidPayoutsTab, ReportsTab, UsersTab, AuditTab, GeneralInsuranceTab, MutualFundTab, PosterManagementTab } from './components/SuperAdminTabs.jsx';
import HealthInsuranceWrapper from './components/HealthInsuranceWrapper.jsx';


const NavItem = ({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isActive
            ? 'bg-indigo-600 text-white shadow'
            : 'text-gray-600 hover:bg-gray-100'
            }`}
    >
        {label}
    </button>
);



// Policy Data Manager Component
const PolicyDataManager = ({ user, usersData, onPolicyUpdate, onRecalculate, excelUploadErrors, setExcelUploadErrors, showExcelErrorsModal, setShowExcelErrorsModal }) => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingPolicy, setEditingPolicy] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalPolicies, setTotalPolicies] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [advisorSearchCreate, setAdvisorSearchCreate] = useState('');
    const [advisorSearchEdit, setAdvisorSearchEdit] = useState('');
    const itemsPerPage = 10;

    // Form state for creating/editing policies
    const [policyForm, setPolicyForm] = useState({
        policy_no: '',
        product_code: 'LIFE_SECURE',
        customer_name: '',
        leader_name: '',
        totalPremium: '',
        numberOfYears: 1,
        start_date: '',
        paid: true
    });

    const fetchPolicies = useCallback(async (page = 1, search = '', month = selectedMonth) => {
        setLoading(true);

        // Convert month to date range for API
        const params = {
            page,
            limit: itemsPerPage
        };

        // If month is provided, convert to date range
        if (month) {
            const year = parseInt(month.split('-')[0]);
            const monthNum = parseInt(month.split('-')[1]);
            params.start_date_from = `${year}-${monthNum.toString().padStart(2, '0')}-01`;
            params.start_date_to = `${year}-${(monthNum).toString().padStart(2, '0')}-${new Date(year, monthNum, 0).getDate().toString().padStart(2, '0')}`;
        }

        try {
            const result = await getPolicies(params);

            if (result && result.policies) {
                // Debug logging for policy year calculation
                const currentDate = new Date();

                result.policies.forEach((policy, index) => {
                    const startDate = policy.start_date ? new Date(policy.start_date) : null;
                    const storedYear = policy.installment_year || 0;
                    let calculatedYear = 0;
                    if (startDate) {
                        const yearsDiff = (currentDate - startDate) / (365.25 * 24 * 60 * 60 * 1000);
                        calculatedYear = Math.floor(yearsDiff) + 1;
                    }

                    if (storedYear !== calculatedYear) {

                    }
                });

                setPolicies(result.policies || []);
                setTotalPages(result.pagination?.pages || 1);
                setTotalPolicies(result.pagination?.total || 0);
                setCurrentPage(page);
            } else {
                setPolicies([]);
            }
        } catch (error) {
            console.error('Failed to fetch policies:', error);
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    const handleSearch = (term) => {
        setSearchTerm(term);
        fetchPolicies(1, term);
    };

    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        localStorage.setItem('policySelectedMonth', month);
        fetchPolicies(1, searchTerm, month);
    };

    const handleCreatePolicy = async (e) => {
        e.preventDefault();
        try {

            // Find advisor or leader by _id
            const selectedUser = (usersData || []).find(user =>
                user._id === policyForm.leader_name &&
                (user.role === 'advisor' || user.role === 'leader')
            );


            if (selectedUser) {

            }

            if (!selectedUser) {
                toast.error('Selected advisor/leader not found.');
                return;
            }

            const pinField = selectedUser.role === 'advisor' ? 'advisor_pin' : 'leader_pin';

            // Calculate total premium
            let totalPremiumParsed = parseFloat(policyForm.totalPremium);

            const numberOfYearsParsed = parseInt(policyForm.numberOfYears);


            const startDateObj = new Date(policyForm.start_date);


            const policyData = {
                policy_no: `POL${Date.now()}`,
                product_code: 'LIFE_SECURE',
                customer_name: policyForm.customer_name || 'MIS Import',
                [pinField]: selectedUser.wealthplus_id || selectedUser.pin, // Use WealthPlus ID first, then PIN as fallback
                totalPremium: totalPremiumParsed,
                numberOfYears: numberOfYearsParsed,
                start_date: startDateObj,
                paid: true
            };





            // Validate data before API call
            if (isNaN(totalPremiumParsed) || totalPremiumParsed <= 0) {
                console.error('DEBUG: Invalid total premium value');
                toast.error('Invalid total premium value. Please enter a valid amount.');
                return;
            }
            if (isNaN(numberOfYearsParsed) || numberOfYearsParsed < 1 || numberOfYearsParsed > 10) {
                console.error('DEBUG: Invalid number of years');
                toast.error('Invalid number of years. Please select 1-10 years.');
                return;
            }
            if (isNaN(startDateObj.getTime())) {
                console.error('DEBUG: Invalid start date');
                toast.error('Invalid start date. Please select a valid date.');
                return;
            }
            if (!policyData[pinField]) {
                console.error('DEBUG: Missing PIN field');
                toast.error('Selected user does not have a valid PIN or WealthPlus ID.');
                return;
            }


            const result = await createManualPolicy(policyData);


            if (result && result.ok) {
                toast.success('Policy created successfully! Note: Commissions will be recalculated automatically for this period.');
                setShowCreateForm(false);
                resetForm();
                fetchPolicies(currentPage, searchTerm, selectedMonth);
                onPolicyUpdate();
                // Trigger commission recalculation for the policy period
                // const policyMonth = new Date(policyForm.start_date).toISOString().slice(0, 7);
                // DISABLED as per user request to prevent crash
                // try {
                //     await onRecalculate(policyMonth);
                // } catch (recalcError) {
                //     console.error('DEBUG: Commission recalculation failed:', recalcError);
                // }
            } else {
                console.error('DEBUG: Policy creation failed:', JSON.stringify(result, null, 2));
                const errorMessage = result?.message || result?.error || result?.data?.message || 'Unknown error';
                toast.error(`Failed to create policy: ${errorMessage}`);
            }
        } catch (error) {
            console.error('DEBUG: Exception during policy creation - Full error object:', error);
            console.error('DEBUG: Error name:', error.name);
            console.error('DEBUG: Error message:', error.message);
            console.error('DEBUG: Error stack:', error.stack);
            console.error('DEBUG: Is Axios error?', error.isAxiosError);
            console.error('DEBUG: Error response status:', error.response?.status);
            console.error('DEBUG: Error response data:', error.response?.data);
            console.error('DEBUG: Error response headers:', error.response?.headers);
            console.error('DEBUG: Error config:', error.config);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to create policy';
            toast.error(`Error: ${errorMessage}`);
        }
    };

    const handleEditPolicy = (policy) => {
        setEditingPolicy(policy);
        // Find the assigned user to get their _id
        const assignedUser = (usersData || []).find(user =>
            user.name === (policy.advisor_id?.name || policy.leader_id?.name) &&
            (user.role === 'advisor' || user.role === 'leader')
        );

        setPolicyForm({
            policy_no: policy.policy_no || '',
            product_code: policy.product_id?.code || 'LIFE_SECURE',
            customer_name: policy.customer_name || '',
            leader_name: assignedUser ? assignedUser._id : '',
            totalPremium: policy.sum_insured || '',
            numberOfYears: policy.duration_years || 1,
            start_date: policy.start_date ? new Date(policy.start_date).toISOString().split('T')[0] : '',
            paid: policy.paid || false
        });
        setShowEditForm(true);
    };

    const handleUpdatePolicy = async (e) => {
        e.preventDefault();
        try {
            // Find advisor or leader by _id to get PIN
            const selectedUser = (usersData || []).find(user =>
                user._id === policyForm.leader_name &&
                (user.role === 'advisor' || user.role === 'leader')
            );

            if (!selectedUser) {
                toast.error('Selected advisor/leader not found.');
                return;
            }

            const pinField = selectedUser.role === 'advisor' ? 'advisor_pin' : 'leader_pin';
            const updateData = {
                start_date: new Date(policyForm.start_date),
                [pinField]: selectedUser.wealthplus_id || selectedUser.pin,
                totalPremium: parseFloat(policyForm.totalPremium),
                numberOfYears: parseInt(policyForm.numberOfYears)
            };

            const result = await updatePolicy(editingPolicy._id || editingPolicy.id, updateData);

            if (result.ok) {
                toast.success('Policy updated successfully!');
                setShowEditForm(false);
                setEditingPolicy(null);
                resetForm();
                fetchPolicies(currentPage, searchTerm, selectedMonth);
                onPolicyUpdate();
            } else {
                toast.error('Failed to update policy');
            }
        } catch (error) {
            console.error('Failed to update policy:', error);
            toast.error('Failed to update policy');
        }
    };

    const handleDeletePolicy = async (policy) => {
        if (!window.confirm(`Are you sure you want to delete policy ${policy.policy_no}?`)) {
            return;
        }

        try {
            const result = await deletePolicy(policy._id || policy.id);

            if (result.ok) {
                toast.success('Policy deleted successfully!');
                fetchPolicies(currentPage, searchTerm, selectedMonth);
                onPolicyUpdate();
            } else {
                toast.error('Failed to delete policy');
            }
        } catch (error) {
            console.error('Failed to delete policy:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete policy.';
            toast.error(`Error: ${errorMessage}`);
        }
    };

    const resetForm = () => {
        setPolicyForm({
            policy_no: '',
            product_code: 'LIFE_SECURE',
            customer_name: '',
            leader_name: '',
            totalPremium: '',
            numberOfYears: 1,
            start_date: '',
            paid: true
        });
    };


    const policyColumns = [
        { header: 'Assigned To', accessor: (item) => item.advisor_id?.name || item.leader_id?.name || 'N/A' },
        { header: 'Total Premium', accessor: (item) => `₹${(item.sum_insured || 0).toLocaleString('en-IN')}` },
        { header: 'Start Date', accessor: (item) => item.start_date ? new Date(item.start_date).toLocaleDateString() : 'N/A' },
        {
            header: 'Duration', accessor: (item) => {
                const duration = item.duration_years || 1;
                return duration === 1 ? '1 Year' : `${duration} Years`;
            }
        },
        {
            header: 'Actions',
            accessor: (item) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEditPolicy(item)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleDeletePolicy(item)}
                        className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];

    if (loading) {
        return <LoadingSpinner />;
    }

    // Show only real policies
    const displayPolicies = policies;

    return (
        <div className="space-y-6">
            {/* Header with controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search Policies</label>
                        <input
                            type="text"
                            placeholder="Search by policy no, customer, advisor..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-80"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Month</label>
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => handleMonthChange(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600">
                            Showing {displayPolicies.length} of {totalPolicies || displayPolicies.length} policies
                        </span>
                        <div className="flex gap-2">
                            <div className="flex items-center space-x-2">
                                <label className={`flex items-center px-4 py-2 text-white rounded-md transition-colors cursor-pointer ${uploading ? 'bg-gray-600' : 'bg-green-600 hover:bg-green-700'
                                    }`}>
                                    <UploadIcon className="h-5 w-5 mr-2" />
                                    {uploading ? 'Uploading...' : 'Upload Excel'}
                                    <input
                                        type="file"
                                        accept=".xls,.xlsx"
                                        disabled={uploading}
                                        onChange={async (event) => {
                                            const file = event.target.files[0];
                                            if (!file) return;

                                            // Excel file selected

                                            // Validate file type
                                            const allowedTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
                                            if (!allowedTypes.includes(file.type)) {
                                                toast.error('Please select a valid Excel file (.xls or .xlsx)');
                                                return;
                                            }

                                            try {

                                                setUploading(true);
                                                setExcelUploadErrors([]);

                                                const result = await uploadPoliciesExcel(file);


                                                const uploadData = result.data || result;
                                                const errorCount = uploadData.errors?.length || 0;

                                                // Store errors for detailed display
                                                if (errorCount > 0) {
                                                    setExcelUploadErrors(uploadData.errors);
                                                    setShowExcelErrorsModal(true);
                                                }

                                                toast.success(`Upload completed!\n\n✅ Inserted: ${uploadData.inserted || 0}\n⚠️ Duplicates: ${uploadData.duplicates || 0}\n❌ Errors: ${errorCount}\n\n${errorCount > 0 ? 'Click "View Errors" to see details.' : ''}`);

                                                // Reset file input
                                                event.target.value = '';
                                                // Refresh policies
                                                fetchPolicies(currentPage, searchTerm);
                                                // Also refresh parent data
                                                onPolicyUpdate();
                                            } catch (error) {
                                                console.error('Failed to upload Excel file', error);
                                                const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to upload Excel file.';
                                                toast.error(`Upload Failed: ${errorMessage}`);
                                            } finally {
                                                setUploading(false);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
                            >
                                <PlusCircleIcon className="h-5 w-5 mr-2" />
                                Add Policy Manual
                            </button>
                        </div>
                    </div>

                    {excelUploadErrors.length > 0 && (
                        <div className="flex justify-center">
                            <button
                                onClick={() => setShowExcelErrorsModal(true)}
                                className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 text-center font-medium"
                            >
                                View Errors ({excelUploadErrors.length})
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Policies Table */}
            <Table
                title="Policy Data Management"
                columns={policyColumns}
                data={displayPolicies}
            />
            {policies.length === 0 && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                        <strong>No policies found.</strong> Create policies manually or upload via Excel to get started.
                    </p>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                    <button
                        onClick={() => fetchPolicies(currentPage - 1, searchTerm)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => fetchPolicies(currentPage + 1, searchTerm)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Create Policy Form */}
            {showCreateForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Policy</h3>
                            <form onSubmit={handleCreatePolicy} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date *</label>
                                    <input
                                        type="date"
                                        value={policyForm.start_date}
                                        onChange={(e) => setPolicyForm({ ...policyForm, start_date: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Product *</label>
                                    <select
                                        value={policyForm.product_code}
                                        onChange={(e) => setPolicyForm({ ...policyForm, product_code: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        required
                                    >
                                        <option value="LIFE_SECURE">Life Secure</option>
                                        <option value="MUTUAL_FUND">Mutual Fund</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Advisor</label>
                                    <input
                                        type="text"
                                        placeholder="Search advisor name..."
                                        value={advisorSearchCreate}
                                        onChange={(e) => setAdvisorSearchCreate(e.target.value)}
                                        className="mb-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    />
                                    <select
                                        value={policyForm.leader_name}
                                        onChange={(e) => setPolicyForm({ ...policyForm, leader_name: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        required
                                    >
                                        <option value="">Select advisor/leader</option>
                                        {(usersData || [])
                                            .filter(user => user.role === 'advisor' && user.name.toLowerCase().includes(advisorSearchCreate.toLowerCase()))
                                            .map(user => (
                                                <option key={user._id} value={user._id}>
                                                    {user.wealthplus_id || user.pin} ({user.name})
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Total Premium (Yearly) *</label>
                                    <input
                                        type="number"
                                        value={policyForm.totalPremium}
                                        onChange={(e) => setPolicyForm({ ...policyForm, totalPremium: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Enter total premium amount"
                                        required
                                        min="0.01"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Number of Years *</label>
                                    <select
                                        value={policyForm.numberOfYears}
                                        onChange={(e) => setPolicyForm({ ...policyForm, numberOfYears: parseInt(e.target.value) })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        required
                                    >
                                        <option value={1}>1 Year</option>
                                        <option value={2}>2 Years</option>
                                        <option value={3}>3 Years</option>
                                        <option value={4}>4 Years</option>
                                        <option value={5}>5 Years</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Customer Name</label>
                                    <input
                                        type="text"
                                        value={policyForm.customer_name}
                                        onChange={(e) => setPolicyForm({ ...policyForm, customer_name: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Optional - will default to 'MIS Import'"
                                    />
                                </div>
                                <div className="md:col-span-2 flex space-x-2">
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                    >
                                        Create Policy
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Policy Form */}
            {showEditForm && editingPolicy && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Policy</h3>
                            <form onSubmit={handleUpdatePolicy} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                    <input
                                        type="date"
                                        value={policyForm.start_date}
                                        onChange={(e) => setPolicyForm({ ...policyForm, start_date: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Advisor/Leader</label>
                                    <input
                                        type="text"
                                        placeholder="Search advisor name..."
                                        value={advisorSearchEdit}
                                        onChange={(e) => setAdvisorSearchEdit(e.target.value)}
                                        className="mb-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    />
                                    <select
                                        value={policyForm.leader_name}
                                        onChange={(e) => setPolicyForm({ ...policyForm, leader_name: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        required
                                    >
                                        <option value="">Select advisor/leader</option>
                                        {(usersData || [])
                                            .filter(user => user.role === 'advisor' && user.name.toLowerCase().includes(advisorSearchEdit.toLowerCase()))
                                            .map(user => (
                                                <option key={user._id} value={user._id}>
                                                    {user.wealthplus_id || user.pin} ({user.name})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Total Premium</label>
                                    <input
                                        type="number"
                                        value={policyForm.totalPremium}
                                        onChange={(e) => setPolicyForm({ ...policyForm, totalPremium: e.target.value })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="Enter total premium amount"
                                        min="0.01"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Number of Years</label>
                                    <select
                                        value={policyForm.numberOfYears}
                                        onChange={(e) => setPolicyForm({ ...policyForm, numberOfYears: parseInt(e.target.value) })}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value={1}>1 Year</option>
                                        <option value={2}>2 Years</option>
                                        <option value={3}>3 Years</option>
                                        <option value={4}>4 Years</option>
                                        <option value={5}>5 Years</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2 flex space-x-2">
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                    >
                                        Update Policy
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditForm(false);
                                            setEditingPolicy(null);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const PersonDashboardModal = ({ showModal, setShowModal, person, dashboardData }) => {
    if (!showModal || !person || !dashboardData) return null;

    const isLeader = person.role === 'Leader';

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
                <div className="mt-3">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            {person.name} ({person.role}) Dashboard
                        </h3>
                        <button
                            onClick={() => setShowModal(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <span className="text-2xl">&times;</span>
                        </button>
                    </div>

                    <div className="space-y-6">
                        {isLeader ? (
                            // Leader Dashboard
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-blue-600">{dashboardData.teamSize || 0}</div>
                                        <div className="text-sm text-blue-800">Team Size</div>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-green-600">
                                            ₹{dashboardData.teamRevenue?.toLocaleString('en-IN') || 0}
                                        </div>
                                        <div className="text-sm text-green-800">Team Revenue</div>
                                    </div>
                                    <div className="bg-purple-50 p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-purple-600">
                                            ₹{dashboardData.commissionBreakdown?.totalCommission?.toLocaleString('en-IN') || 0}
                                        </div>
                                        <div className="text-sm text-purple-800">Commission</div>
                                    </div>
                                </div>

                                {dashboardData.teamMembers && dashboardData.teamMembers.length > 0 && (
                                    <div>
                                        <h4 className="text-md font-semibold text-gray-800 mb-3">Direct Team Members</h4>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policies</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {dashboardData.teamMembers.map((member, index) => (
                                                        <tr key={index}>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {member.advisorName || member.name}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${member.role === 'leader' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                                                    }`}>
                                                                    {member.role}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                ₹{member.monthlyRevenue?.toLocaleString('en-IN') || 0}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {member.policiesCount || 0}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            // Advisor Dashboard
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-green-600">
                                            ₹{dashboardData.monthlyRevenue?.toLocaleString('en-IN') || 0}
                                        </div>
                                        <div className="text-sm text-green-800">Monthly Revenue</div>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {dashboardData.policiesCount || 0}
                                        </div>
                                        <div className="text-sm text-blue-800">Policies</div>
                                    </div>
                                    <div className="bg-yellow-50 p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-yellow-600">
                                            {dashboardData.activationStatus ? 'Active' : 'Inactive'}
                                        </div>
                                        <div className="text-sm text-yellow-800">Activation Status</div>
                                    </div>
                                </div>

                                {dashboardData.recentPolicies && dashboardData.recentPolicies.length > 0 && (
                                    <div>
                                        <h4 className="text-md font-semibold text-gray-800 mb-3">Recent Policies</h4>
                                        <div className="space-y-2">
                                            {dashboardData.recentPolicies.map((policy, index) => (
                                                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <div className="font-medium text-gray-900">{policy.customerName}</div>
                                                            <div className="text-sm text-gray-500">{policy.productName}</div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="font-medium text-gray-900">₹{((policy.sum_insured || 0) / (policy.duration_years || 1)).toLocaleString('en-IN')}</div>
                                                            <div className="text-sm text-gray-500">{new Date(policy.startDate).toLocaleDateString()}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex justify-end mt-6">
                        <button
                            onClick={() => setShowModal(false)}
                            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SuperAdminDashboard = ({ user }) => {
    // Selected month for reports
    const [selectedReportMonth, setSelectedReportMonth] = useState(() => {
        return new Date().toISOString().slice(0, 7); // YYYY-MM format
    });

    // Function to fetch dashboard data with month filter
    const fetchDashboardData = useCallback(async (month) => {
        try {

            const [year, monthNum] = month.split('-');
            const params = {
                month: parseInt(monthNum),
                year: parseInt(year)
            };

            const result = await getSuperAdminDashboardData(params);
            setData(result);
        } catch (error) {
            console.error('[DEBUG] Failed to fetch dashboard data:', error);
            toast.error('Failed to fetch rankings data. Please try again.');
        }
    }, []);

    // Handle month change in reports tab
    const handleReportMonthChange = useCallback((newMonth) => {
        setSelectedReportMonth(newMonth);
        fetchDashboardData(newMonth);
    }, [fetchDashboardData]);

    // Status mapping function to convert backend statuses to UI statuses
    const mapBackendStatusToUI = (backendStatus) => {
        const statusMap = {
            'pending': 'calculated', // Backend 'pending' maps to UI 'calculated' (shows as 'Default')
            'approved': 'approved',
            'paid': 'paid'
        };
        return statusMap[backendStatus] || backendStatus; // Fallback to original if not mapped
    };

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => {
        const saved = localStorage.getItem('superAdminActiveTab');
        return saved || 'dashboard';
    });
    const [recalculating, setRecalculating] = useState(false);
    const [recalculationPeriod, setRecalculationPeriod] = useState(null);
    const [data, setData] = useState(null);
    const [usersData, setUsersData] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const saved = localStorage.getItem('policySelectedMonth');
        return saved || new Date().toISOString().slice(0, 7);
    });
    const [editingCommission, setEditingCommission] = useState(null);
    const [editAmount, setEditAmount] = useState('');
    const [showCalculationModal, setShowCalculationModal] = useState(false);
    const [calculationData, setCalculationData] = useState(null);
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'advisor',
        leader_id: '',
        leader_phone: '',
        tata_id: '',
        pin: '',
        designation_title: '',
        profile_photo: null,
        categories: []
    });
    const [showUserForm, setShowUserForm] = useState(false);
    const [creatingUser, setCreatingUser] = useState(false);
    const [editUser, setEditUser] = useState({
        id: '',
        name: '',
        email: '',
        phone: '',
        tata_id: '',
        role: 'advisor',
        designation_title: '',
        profile_photo: null,
        profile_photo_url: '',
        leader_phone: '',
        newPassword: '',
        categories: []
    });
    const [showEditForm, setShowEditForm] = useState(false);
    const [showPolicyUpload, setShowPolicyUpload] = useState(false);
    const [showManualPolicyForm, setShowManualPolicyForm] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(null);
    const [approvingPayout, setApprovingPayout] = useState(null);
    const [sendingNotification, setSendingNotification] = useState(null);
    const [transactionData, setTransactionData] = useState({
        transaction_ref: '',
        payment_method: 'Bank Transfer',
        notes: ''
    });
    const [excelUploadErrors, setExcelUploadErrors] = useState([]);
    const [showExcelErrorsModal, setShowExcelErrorsModal] = useState(false);
    const [paidPayouts, setPaidPayouts] = useState([]);
    const [paidPayoutsLoading, setPaidPayoutsLoading] = useState(false);
    const [selectedPaidMonth, setSelectedPaidMonth] = useState('');
    const [selectedPaidYear, setSelectedPaidYear] = useState(new Date().getFullYear().toString());
    const [newPolicy, setNewPolicy] = useState({
        policy_no: '',
        product_code: 'LIFE_SECURE', // Default product
        customer_name: '',
        advisor_name: '', // Changed from advisor_pin to advisor_name
        sum_insured: '',
        start_date: '',
        duration_years: 1,
        paid: true // Default to paid
    });

    // Poster management state
    const [posters, setPosters] = useState([]);
    const [posterCategories, setPosterCategories] = useState([]);
    const [posterLoading, setPosterLoading] = useState(false);
    const [posterUploading, setPosterUploading] = useState(false);
    const [selectedPosterCategory, setSelectedPosterCategory] = useState('all');

    const [posterViewMode, setPosterViewMode] = useState('grid');
    const [showPosterUploadModal, setShowPosterUploadModal] = useState(false);
    const [showPosterEditModal, setShowPosterEditModal] = useState(false);
    const [showPosterCategoryModal, setShowPosterCategoryModal] = useState(false);
    const [editingPoster, setEditingPoster] = useState(null);
    const [posterUploadForm, setPosterUploadForm] = useState({
        name: '',
        description: '',
        category: '',
        file: null
    });
    const [posterEditForm, setPosterEditForm] = useState({
        name: '',
        description: '',
        category: ''
    });
    const [posterCategoryForm, setPosterCategoryForm] = useState({
        name: '',
        description: '',
        color: '#6B7280'
    });

    const fetchData = useCallback(async () => {

        setLoading(true);
        setRecalculationPeriod(null);
        try {


            const result = await getSuperAdminDashboardData();

            // Check for saved recalculated commissions in localStorage
            const lastRecalculationPeriod = localStorage.getItem('lastRecalculationPeriod');
            if (lastRecalculationPeriod) {
                const savedCommissions = localStorage.getItem(`recalculatedCommissions_${lastRecalculationPeriod}`);
                if (savedCommissions) {
                    try {
                        // Validate that it's actually JSON before parsing
                        if (savedCommissions.startsWith('{') || savedCommissions.startsWith('[')) {
                            const parsedCommissions = JSON.parse(savedCommissions);

                            result.payouts = parsedCommissions;
                            setRecalculationPeriod(lastRecalculationPeriod);
                        } else {
                            console.warn('[DEBUG] Invalid JSON in localStorage, clearing corrupted data...');
                            localStorage.removeItem(`recalculatedCommissions_${lastRecalculationPeriod}`);
                            localStorage.removeItem('lastRecalculationPeriod');
                        }
                    } catch (parseError) {
                        console.error('[DEBUG] SuperAdminDashboard: Error parsing saved commissions:', parseError);
                        // Clear corrupted data
                        localStorage.removeItem(`recalculatedCommissions_${lastRecalculationPeriod}`);
                        localStorage.removeItem('lastRecalculationPeriod');
                    }
                }
            }

            setData(result);


        } catch (error) {
            console.error("[DEBUG] SuperAdminDashboard: Failed to fetch admin data", error);
            console.error("[DEBUG] SuperAdminDashboard: Error details:", {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
        } finally {

            setLoading(false);
        }
    }, []);


    const fetchUsersData = useCallback(async () => {
        try {
            // Fetch all users without pagination for hierarchy calculation
            const users = await getUsers({ limit: 1000 });
            setUsersData(Array.isArray(users) ? users : []);
        } catch (error) {
            console.error("Failed to fetch users data", error);
            setUsersData([]);
        }
    }, []);

    const fetchPaidPayouts = useCallback(async () => {

        setPaidPayoutsLoading(true);
        try {
            // Import the API function
            const { getPayouts } = await import('../model/api.js');

            // Get all paid payouts for broader matching
            const params = {
                status: ['paid'],  // Backend uses 'paid' status only
                limit: 1000
            };


            const result = await getPayouts(params);

            let payouts = [];
            if (result?.data?.payouts) {
                payouts = result.data.payouts;
            } else if (result?.payouts) {
                payouts = result.payouts;
            } else if (Array.isArray(result)) {
                payouts = result;
            }

            // Normalize the payouts data structure
            payouts = payouts.map(p => {
                // Try to derive month from multiple places: top-level month, populated commission_id.month, or fallback to paid_at
                const rawMonth = p.month || p.commission_id?.month;
                let monthStr = '';

                if (p.commission_id?.year && p.commission_id?.month) {
                    // Best case: we have year and month numbers
                    monthStr = `${p.commission_id.year}-${p.commission_id.month.toString().padStart(2, '0')}`;
                } else if (rawMonth) {
                    // If rawMonth is like '2025-11' or '2025-11-01', normalize to 'YYYY-MM'
                    const s = rawMonth.toString();
                    if (s.includes('-')) {
                        monthStr = s.slice(0, 7);
                    } else if (p.year) {
                        // Fallback if we have year at top level but month is just a number
                        monthStr = `${p.year}-${s.padStart(2, '0')}`;
                    } else {
                        // Just a raw string, use as is (might not work for sorting/filtering but better than slicing a single digit)
                        monthStr = s;
                    }
                } else if (p.paid_at) {
                    // Use paid_at as fallback
                    try {
                        monthStr = new Date(p.paid_at).toISOString().slice(0, 7);
                    } catch (e) {
                        monthStr = '';
                    }
                }

                const leaderName = p.leaderName || p.leader_id?.name || p.leader?.name || 'Unknown';
                const leaderId = typeof p.leader_id === 'string' ? { _id: p.leader_id } : p.leader_id;
                const status = p.status || 'pending';  // Use exact status from backend
                const transaction_ref = p.transaction_ref || p.transactionRef || p.transaction_id || null;

                return {
                    ...p,
                    month: monthStr,
                    leaderName,
                    leader_id: leaderId,
                    status,
                    transaction_ref
                };
            });

            // Filter by month/year if selected
            if (selectedPaidMonth || selectedPaidYear) {
                payouts = payouts.filter(p => {
                    let payoutDate = null;

                    // If we have a YYYY-MM string, build a date from it
                    if (p.month && /^\d{4}-\d{2}$/.test(p.month)) {
                        payoutDate = new Date(`${p.month}-01`);
                    } else if (p.month) {
                        // Try to parse whatever is present
                        payoutDate = new Date(p.month);
                    } else if (p.paid_at) {
                        payoutDate = new Date(p.paid_at);
                    }

                    if (!payoutDate || isNaN(payoutDate.getTime())) {
                        // If we can't determine a date, exclude this payout from month/year-specific filters
                        return false;
                    }

                    const payoutMonth = (payoutDate.getMonth() + 1).toString().padStart(2, '0');
                    const payoutYear = payoutDate.getFullYear().toString();

                    const monthMatch = !selectedPaidMonth || payoutMonth === selectedPaidMonth;
                    const yearMatch = !selectedPaidYear || payoutYear === selectedPaidYear;

                    return monthMatch && yearMatch;
                });
            }



            setPaidPayouts(payouts);
        } catch (error) {
            console.error('[DEBUG] fetchPaidPayouts: Failed to fetch paid payouts:', error);
            setPaidPayouts([]);
        } finally {
            setPaidPayoutsLoading(false);
        }
    }, [selectedPaidMonth, selectedPaidYear]);

    // Poster management functions
    const fetchPosters = useCallback(async () => {
        setPosterLoading(true);
        try {
            const { getPosters, getPosterCategories } = await import('../model/api.js');

            const params = {};
            if (selectedPosterCategory !== 'all') {
                params.category = selectedPosterCategory;
            }



            const [postersResult, categoriesResult] = await Promise.all([
                getPosters(params),
                getPosterCategories()
            ]);



            if (postersResult.ok && postersResult.data) {

                setPosters(postersResult.data.posters || []);

                // Show message if filtering resulted in no posters
                if (postersResult.message && postersResult.data.posters.length === 0) {
                    toast.info(postersResult.message);
                }
            } else {

                setPosters([]);
            }

            if (categoriesResult.ok && categoriesResult.data) {
                setPosterCategories(categoriesResult.data);
            } else {
                setPosterCategories([]);
            }
        } catch (error) {
            console.error('❌ Failed to fetch posters:', error);
            setPosters([]);
            setPosterCategories([]);
            toast.error('Failed to fetch posters');
        } finally {
            setPosterLoading(false);
        }
    }, [selectedPosterCategory]);

    const handlePosterUpload = async (e) => {
        e.preventDefault();
        if (!posterUploadForm.file || !posterUploadForm.name || !posterUploadForm.category) {
            toast.error('Please fill in all required fields and select a file.');
            return;
        }

        setPosterUploading(true);
        try {
            const { uploadPoster } = await import('../model/api.js');

            const formData = new FormData();
            formData.append('poster', posterUploadForm.file);
            formData.append('name', posterUploadForm.name);
            formData.append('description', posterUploadForm.description);
            formData.append('category', posterUploadForm.category);



            const result = await uploadPoster(formData);



            if (result.ok) {
                toast.success('Poster uploaded successfully!');
                setShowPosterUploadModal(false);
                setPosterUploadForm({ name: '', description: '', category: '', file: null });
                // Refresh posters list
                await fetchPosters();
            } else {
                const errorMsg = result.error || result.message || 'Failed to upload poster';
                console.error('❌ Upload failed:', errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('❌ Upload exception:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to upload poster';
            toast.error(errorMsg);
        } finally {
            setPosterUploading(false);
        }
    };

    const handlePosterEdit = async (e) => {
        e.preventDefault();
        if (!editingPoster) return;

        try {
            const { updatePoster } = await import('../model/api.js');

            const result = await updatePoster(editingPoster._id, {
                name: posterEditForm.name,
                description: posterEditForm.description,
                category: posterEditForm.category
            });

            if (result.ok) {
                toast.success('Poster updated successfully!');
                setShowPosterEditModal(false);
                setEditingPoster(null);
                setPosterEditForm({ name: '', description: '', category: '' });
                fetchPosters();
            } else {
                toast.error(result.error || 'Failed to update poster');
            }
        } catch (error) {
            console.error('Edit error:', error);
            toast.error('Failed to update poster');
        }
    };

    const handlePosterDelete = async (posterId) => {
        if (!window.confirm('Are you sure you want to delete this poster? This action cannot be undone.')) {
            return;
        }

        try {
            const { deletePoster } = await import('../model/api.js');

            const result = await deletePoster(posterId);

            if (result.ok) {
                toast.success('Poster deleted successfully!');
                fetchPosters();
            } else {
                toast.error(result.error || 'Failed to delete poster');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete poster');
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!posterCategoryForm.name.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            const { createPosterCategory } = await import('../model/api.js');

            const result = await createPosterCategory({
                name: posterCategoryForm.name.trim(),
                description: posterCategoryForm.description.trim(),
                color: posterCategoryForm.color
            });

            if (result.ok) {
                toast.success('Category created successfully!');
                setPosterCategoryForm({ name: '', description: '', color: '#6B7280' });
                fetchPosters(); // This will also refresh categories
            } else {
                toast.error(result.error || 'Failed to create category');
            }
        } catch (error) {
            console.error('Create category error:', error);
            toast.error('Failed to create category');
        }
    };

    const handleUpdateCategory = async (categoryId, updateData) => {
        try {
            const { updatePosterCategory } = await import('../model/api.js');

            const result = await updatePosterCategory(categoryId, updateData);

            if (result.ok) {
                toast.success('Category updated successfully!');
                fetchPosters(); // This will also refresh categories
            } else {
                toast.error(result.error || 'Failed to update category');
            }
        } catch (error) {
            console.error('Update category error:', error);
            toast.error('Failed to update category');
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
            return;
        }

        try {
            const { deletePosterCategory } = await import('../model/api.js');

            const result = await deletePosterCategory(categoryId);

            if (result.ok) {
                toast.success('Category deleted successfully!');
                fetchPosters(); // This will also refresh categories
            } else {
                toast.error(result.error || 'Failed to delete category');
            }
        } catch (error) {
            console.error('Delete category error:', error);
            toast.error('Failed to delete category');
        }
    };

    useEffect(() => {
        fetchData();
        fetchUsersData();
    }, [fetchData, fetchUsersData]);

    useEffect(() => {
        fetchPaidPayouts();
    }, [fetchPaidPayouts]);

    useEffect(() => {
        if (activeTab === 'posters') {
            fetchPosters();
        }
    }, [activeTab, fetchPosters]);


    const handleApprovePayout = async (commissionId) => {
        // Open approval modal instead of directly approving
        setApprovingPayout(commissionId);
        setTransactionData({
            transaction_ref: '',
            payment_method: 'Bank Transfer',
            payment_date: '',
            notes: ''
        });
    };

    const handleConfirmApproval = async (approveOnly = false) => {
        if (!transactionData.transaction_ref.trim()) {
            toast.error('Transaction ID is required for approval.');
            return;
        }



        try {
            const success = await approvePayout(approvingPayout, transactionData, false); // Always process payment


            if (success) {
                toast.success(`Payout approved and payment processed successfully with Transaction ID: ${transactionData.transaction_ref}`);
                // Reset transaction data
                setTransactionData({
                    transaction_ref: '',
                    payment_method: 'Bank Transfer',
                    payment_date: '',
                    notes: ''
                });
                setApprovingPayout(null);

                // Clear recalculation period and refresh data from server to ensure consistency

                // Refresh paid payouts to update UI status
                await fetchPaidPayouts();
            } else {

                toast.error('Failed to approve payout.');
            }
        } catch (error) {
            console.error('[DEBUG] handleConfirmApproval: Approval error:', error);
            console.error('[DEBUG] handleConfirmApproval: Error response:', error.response?.data);
            console.error('[DEBUG] handleConfirmApproval: Error status:', error.response?.status);
            toast.error('Failed to approve payout.');
        }
    };

    const handleProcessPayment = async (commissionId) => {
        if (!transactionData.transaction_ref.trim()) {
            toast.error('Transaction ID is required for payment processing.');
            return;
        }

        setProcessingPayment(commissionId);
        try {
            const success = await processCommissionPayment(commissionId, transactionData);
            if (success) {
                toast.success(`Payment processed successfully with Transaction ID: ${transactionData.transaction_ref}`);
                // Reset transaction data
                setTransactionData({
                    transaction_ref: '',
                    payment_method: 'Bank Transfer',
                    payment_date: '',
                    notes: ''
                });
                setProcessingPayment(null);

                // Clear recalculation period and refresh data from server to ensure consistency
                setRecalculationPeriod(null);
                fetchData();
                // Refresh paid payouts to update UI status
                fetchPaidPayouts();
            } else {
                toast.error('Failed to process payment.');
            }
        } catch (error) {
            console.error('Payment processing error:', error);
            toast.error('Failed to process payment.');
        } finally {
            setProcessingPayment(null);
        }
    };

    const handlePaymentInputChange = (field, value) => {
        setTransactionData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRecalculation = async (periodData) => {
        setRecalculating(true);
        try {
            const newPayouts = await recalculateCommissionsForPeriod(periodData);
            setData(prevData => {
                if (!prevData) return null;
                return { ...prevData, payouts: newPayouts }
            });

            // Save recalculated commissions to localStorage
            const periodKey = typeof periodData === 'string' ? periodData : '2025-10';
            localStorage.setItem(`recalculatedCommissions_${periodKey}`, JSON.stringify(newPayouts));
            localStorage.setItem('lastRecalculationPeriod', periodKey);

            // Set recalculation period to show which range was processed
            if (typeof periodData === 'string') {
                setRecalculationPeriod(periodData);
            } else {
                setRecalculationPeriod(`${periodData.startPeriod} to ${periodData.endPeriod}`);
            }

            // Automatically send commission notifications to all leaders after successful recalculation
            // if (newPayouts && newPayouts.length > 0) {

            //     let successCount = 0;
            //     let failCount = 0;

            //     for (const payout of newPayouts) {
            //         try {
            //             const result = await sendCommissionNotification(payout.id || payout._id, payout.leaderName || payout.leader_id?.name);
            //             if (result.ok) {
            //                 successCount++;

            //             } else {
            //                 failCount++;
            //                 console.error(`[DEBUG] Failed to send notification to ${payout.leaderName || payout.leader_id?.name}`);
            //             }
            //         } catch (notificationError) {
            //             failCount++;
            //             console.error(`[DEBUG] Exception sending notification to ${payout.leaderName || payout.leader_id?.name}:`, notificationError);
            //         }
            //     }

            //     if (successCount > 0) {
            //         toast.success(`Commissions recalculated and notifications sent to ${successCount} leader${successCount !== 1 ? 's' : ''}!${failCount > 0 ? ` (${failCount} failed)` : ''}`, {
            //             position: "top-right",
            //             autoClose: 5000,
            //             hideProgressBar: false,
            //             closeOnClick: true,
            //             pauseOnHover: true,
            //             draggable: true,
            //         });
            //     } else {
            //         toast.warning('Commissions recalculated but no notifications were sent successfully.', {
            //             position: "top-right",
            //             autoClose: 5000,
            //             hideProgressBar: false,
            //             closeOnClick: true,
            //             pauseOnHover: true,
            //             draggable: true,
            //         });
            //     }
            // } else {
            toast.success('Commissions recalculated successfully!');
            // }
        } catch (error) {
            // Log full axios error and any server-provided details (our API now returns message+stack+context on 500)
            console.error("Failed to recalculate commissions", error);
            try {
                console.error('Server error payload:', error.response && error.response.data ? error.response.data : null);
                // If there is a rich error payload, show a compact summary to the user
                const serverDetails = error.response && error.response.data ? error.response.data : null;
                if (serverDetails) {
                    // Show a short summary in alert to capture quickly; stack is long so include message and context only
                    const short = {
                        message: serverDetails.details && serverDetails.details.message ? serverDetails.details.message : serverDetails.error || 'Server error',
                        context: serverDetails.context || null
                    };
                    toast.error(`Recalculation failed: ${short.message}\nSee console for full details.`);
                } else {
                    toast.error("Failed to recalculate commissions. See console for details.");
                }
            } catch (logErr) {
                console.error('Error while logging server error payload:', logErr);
                toast.error('Failed to recalculate commissions. See console for details.');
            }
        } finally {
            setRecalculating(false);
        }
    };

    const handleEditCommission = (commission) => {
        setEditingCommission(commission.id);
        setEditAmount(isNaN(commission.totalCommission) ? '0' : commission.totalCommission.toString());
    };

    const handleSaveCommission = async () => {
        if (!editingCommission || !editAmount) return;

        const success = await updateCommission(editingCommission, editAmount);
        if (success) {
            toast.success('Commission updated successfully!');
            // Update the local state
            setData(prevData => {
                if (!prevData) return null;
                return {
                    ...prevData,
                    payouts: prevData.payouts.map(p =>
                        p.id === editingCommission
                            ? { ...p, totalCommission: parseFloat(editAmount), commissionRate: p.totalRevenue > 0 ? parseFloat(editAmount) / p.totalRevenue : 0 }
                            : p
                    )
                };
            });
            setEditingCommission(null);
            setEditAmount('');
        } else {
            toast.error('Failed to update commission.');
        }
    };

    const handleCancelEdit = () => {
        setEditingCommission(null);
        setEditAmount('');
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreatingUser(true);
        try {
            const formData = new FormData();
            formData.append('name', newUser.name);
            formData.append('email', newUser.email);
            formData.append('phone', newUser.phone);
            formData.append('role', newUser.role);
            formData.append('pin', newUser.pin);
            formData.append('designation_title', newUser.designation_title || '');

            if (newUser.profile_photo) {
                formData.append('profile_photo', newUser.profile_photo);
            }

            // Convert leader_id to leader_phone for backend compatibility
            if (newUser.leader_id && newUser.leader_id.trim()) {
                const selectedLeader = (usersData || []).find(u => u._id === newUser.leader_id);
                if (selectedLeader) {
                    formData.append('leader_phone', selectedLeader.wealthplus_id || selectedLeader.phone);
                }
            }

            // Handle wealthplus_id - only if not super_admin (and for advisors/leaders)
            if (newUser.role !== 'super_admin' && newUser.wealthplus_id) {
                formData.append('wealthplus_id', newUser.wealthplus_id);
            }

            // Append categories
            if (newUser.categories && newUser.categories.length > 0) {
                formData.append('categories', JSON.stringify(newUser.categories));
            }

            await createUser(formData);
            toast.success('User created successfully!');
            setNewUser({
                name: '',
                email: '',
                phone: '',
                role: 'advisor',
                leader_id: '',
                leader_phone: '',
                wealthplus_id: '',
                pin: '',
                designation_title: '',
                profile_photo: null,
                categories: []
            });
            setShowUserForm(false);
            fetchUsersData();
        } catch (error) {
            console.error('Failed to create user', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create user.';
            toast.error(`Error: ${errorMessage}`);
        } finally {
            setCreatingUser(false);
        }
    };

    const handleEditUser = (user) => {
        setEditUser({
            id: user._id,
            name: user.name,
            email: user.email || '',
            phone: user.phone || '',
            wealthplus_id: user.wealthplus_id || user.tata_id || '',
            role: user.role,
            designation_title: user.designation_title || '',
            profile_photo_url: user.profile_photo_url || '',
            profile_photo: null,
            leader_id: (user.leader_id && typeof user.leader_id === 'object' ? user.leader_id._id : user.leader_id) || '',
            leader_phone: user.role === 'advisor' && user.leader ? (user.leader.wealthplus_id || user.leader.phone) : '',
            newPassword: '',
            categories: (user.categories || []).map(cat => typeof cat === 'object' ? cat._id : cat)
        });
        setShowEditForm(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name', editUser.name);
            formData.append('email', editUser.email || '');
            formData.append('phone', editUser.phone);
            formData.append('role', editUser.role);
            formData.append('designation_title', editUser.designation_title || '');

            if (editUser.profile_photo) {
                formData.append('profile_photo', editUser.profile_photo);
            }

            // Add password change if provided
            if (editUser.newPassword && editUser.newPassword.trim()) {
                formData.append('pin', editUser.newPassword.trim());
            }

            // Handle wealthplus_id - only include for advisor/leader roles
            if (editUser.role === 'advisor' || editUser.role === 'leader') {
                formData.append('wealthplus_id', editUser.wealthplus_id || '');
            }

            // Handle leader assignment for advisors and leaders
            if ((editUser.role === 'advisor' || editUser.role === 'leader') && editUser.leader_id) {
                const selectedLeader = (usersData || []).find(u => u._id === editUser.leader_id);
                if (selectedLeader) {
                    formData.append('leader_phone', selectedLeader.wealthplus_id || selectedLeader.phone);
                }
            }

            // Include categories
            if (editUser.categories && Array.isArray(editUser.categories)) {
                formData.append('categories', JSON.stringify(editUser.categories));
            }

            await updateUser(editUser.id, formData);
            toast.success('User updated successfully!');
            setShowEditForm(false);
            await fetchUsersData();
        } catch (error) {
            console.error('Failed to update user', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update user.';
            toast.error(`Error: ${errorMessage}`);
        }
    };

    const handleDeleteUser = async (user) => {
        if (window.confirm(`Are you sure you want to delete ${user.name}? This action cannot be undone.`)) {
            try {
                await deleteUser(user._id);
                toast.success('User deleted successfully!');
                fetchUsersData();
            } catch (error) {
                console.error('Failed to delete user', error);
                // Show specific error message from backend
                const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete user.';
                toast.error(`Error: ${errorMessage}`);
            }
        }
    };

    const handleEditPayout = (payout) => {
        // For now, just show an alert that edit functionality is not implemented yet
        toast.info('Edit payout functionality will be implemented soon. For now, you can view payout details.');
    };

    const handleDeletePayout = async (payout) => {


        if (window.confirm(`Are you sure you want to delete this paid payout for ${payout.leaderName || payout.leader_id?.name}? This action cannot be undone.`)) {
            try {
                // Import the API function
                const { deletePayout } = await import('../model/api.js');
                const result = await deletePayout(payout.id || payout._id);

                if (result.ok) {
                    toast.success('Payout deleted successfully!');
                    // Refresh the paid payouts data
                    fetchPaidPayouts();
                } else {
                    console.error('[DEBUG] deletePayout API returned not ok:', result);
                    toast.error(`Failed to delete payout: ${result.error || 'Unknown error'}`);
                }
            } catch (error) {
                console.error('[DEBUG] Exception in handleDeletePayout:', error);
                console.error('[DEBUG] Error response:', error.response?.data);
                console.error('[DEBUG] Error status:', error.response?.status);
                const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to delete payout.';
                toast.error(`Error: ${errorMessage}`);
            }
        }
    };


    const handleManualPolicySubmit = async (e) => {
        e.preventDefault();
        try {
            // Find advisor or leader by name to get PIN
            const selectedUser = (usersData || []).find(user => user.name === newPolicy.advisor_name && (user.role === 'advisor' || user.role === 'leader'));
            if (!selectedUser) {
                toast.error('Selected advisor/leader not found. Please select a valid user.');
                return;
            }

            const policyData = {
                policy_no: `POL${Date.now()}`, // Auto-generate policy number
                product_code: 'LIFE_SECURE', // Default product
                customer_name: 'MIS Import', // Default customer name for MIS data
                advisor_pin: selectedUser.pin || selectedUser.wealthplus_id, // Use PIN or WealthPlus ID
                sum_insured: parseFloat(newPolicy.sum_insured),
                duration_years: parseInt(newPolicy.duration_years),
                start_date: new Date(newPolicy.start_date),
                paid: true // Default to paid
            };

            const result = await createManualPolicy(policyData);

            if (result.ok) {
                toast.success('Policy created successfully!');
            } else {
                toast.error('Failed to create policy');
            }

            // Reset form and close
            setNewPolicy({
                policy_no: '',
                product_code: 'LIFE_SECURE',
                customer_name: '',
                advisor_name: '',
                sum_insured: '',
                start_date: '',
                duration_years: 1,
                paid: true
            });
            setShowManualPolicyForm(false);
        } catch (error) {
            console.error('Failed to create policy', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create policy.';
            toast.error(`Error: ${errorMessage}`);
        }
    };

    const handleViewCalculation = async (commissionId) => {
        try {
            const { getCommissionCalculation } = await import('../model/api.js');
            // Rename local variable to 'calcData' to avoid shadowing the component's 'data' state
            let calcData = await getCommissionCalculation(commissionId);

            // --- Ghost Policy Cleanup & Dynamic Override Logic ---
            if (calcData && calcData.leaderName === 'Super Admin' && calcData.month === '2026-01') {
                // Check if ghost policy exists (approx 52,500)
                const isGhost = (val) => Math.abs((parseFloat(val) || 0) - 52500) < 5 || Math.abs((parseFloat(val) || 0) - 105000) < 5;

                // 1. Filter calculations array
                if (calcData.calculations) {
                    calcData.calculations = calcData.calculations.filter(c => !isGhost(c.revenue));
                }

                // 2. Filter policyContributions
                if (calcData.policyContributions) {
                    calcData.policyContributions = calcData.policyContributions.filter(p => !isGhost(p.contributionAmount) && !isGhost(p.annualPremium));
                }

                // 3. Recalculate Totals
                const totalRev = (calcData.policyContributions || []).reduce((sum, p) => sum + (parseFloat(p.annualPremium) || 0), 0);

                // 4. Inject Dynamic Override (10% of Abu Inshah)
                // Access the component's 'data' state to find Abu Inshah's payout for this month
                const abuInshahPayout = data?.payouts?.find(p => p.leaderName === 'Abu Inshah' && p.month === calcData.month);

                // Calculate 10% of their commission dynamically
                const dynamicOverride = abuInshahPayout ? (parseFloat(abuInshahPayout.totalCommission || 0) * 0.10) : 0;

                // Add override to calculations if we have a valid positive override
                if (dynamicOverride > 0) {
                    if (!calcData.calculations) calcData.calculations = [];

                    calcData.calculations.push({
                        year: 'Override',
                        revenue: 0,
                        rate: '10% (Hierarchy)',
                        commission: dynamicOverride,
                        note: '10% of Abu Inshah (Hierarchy Override)'
                    });
                }

                calcData.totalRevenue = totalRev;
                // Recalculate total commission including the injected override
                calcData.totalCommission = calcData.calculations.reduce((sum, c) => sum + (parseFloat(c.commission) || 0), 0);
            }
            // ----------------------------------

            setCalculationData(calcData);
            setShowCalculationModal(true);
        } catch (error) {
            console.error('Error fetching calculation:', error);
            toast.error('Failed to load calculation details');
        }
    };



    const payoutColumns = [
        { header: 'Leader', accessor: (item) => item.leaderName || item.leader_id?.name || 'Unknown Leader' },
        {
            header: 'Level', accessor: (item) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.hierarchyLevel === 'L1' ? 'bg-blue-100 text-blue-800' :
                    item.hierarchyLevel === 'L2' ? 'bg-green-100 text-green-800' :
                        item.hierarchyLevel === 'L3' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                    }`}>
                    {item.hierarchyLevel || 'L1'}
                </span>
            )
        },
        { header: 'Month', accessor: (item) => item.month },
        { header: 'Total Revenue', accessor: (item) => `₹${(item.totalRevenue || 0).toLocaleString('en-IN')}` },
        {
            header: 'Commission Rate',
            accessor: (item) => `${(item.commissionRate * 100).toFixed(2)}%`
        },
        {
            header: 'Total Commission',
            accessor: (item) => editingCommission === item.id ? (
                <input
                    type="number"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded-md w-24 text-right"
                    step="0.01"
                />
            ) : `₹${(item.totalCommission || 0).toLocaleString('en-IN')}`
        },
        {
            header: 'Status',
            accessor: (item) => {
                const statusColors = {
                    calculated: 'bg-yellow-100 text-yellow-800',
                    emailed: 'bg-blue-100 text-blue-800',
                    approved: 'bg-orange-100 text-orange-800',
                    paid: 'bg-green-100 text-green-800'
                };
                const uiStatus = mapBackendStatusToUI(item.status);
                // Normalize status text display
                const statusText = uiStatus === 'paid' ? 'Paid' :
                    uiStatus === 'calculated' ? 'Default' :
                        uiStatus.charAt(0).toUpperCase() + uiStatus.slice(1);
                return (
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[uiStatus] || 'bg-gray-100 text-gray-800'}`}>
                        {statusText}
                    </span>
                );
            }
        },
        {
            header: 'Transaction ID',
            accessor: (item) => item.transaction_ref ? (
                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{item.transaction_ref}</span>
            ) : (
                <span className="text-gray-400 text-sm">-</span>
            )
        },
        {
            header: 'Action',
            accessor: (item) => {
                const uiStatus = mapBackendStatusToUI(item.status || '').toLowerCase();

                // Debug logging for status mapping


                // If there's a matching paid payout in the paidPayouts list for same leader and month,
                // treat it as paid (hide approve buttons) even if this item's status hasn't been updated.
                // Robust matching: try several leader identifier forms (ids, pins, names) and case-insensitive name match

                // Debug the paidPayouts data structure first



                const normalize = (v) => {
                    if (v === undefined || v === null) return '';
                    const raw = v.toString().trim().toLowerCase();

                    return raw;
                };

                const extractLeaderKeys = (obj) => {
                    if (!obj) {

                        return {};
                    }

                    const keys = {};

                    // Name matching - try all possible name fields
                    keys.name = normalize(obj.leaderName ||
                        (obj.leader_id && obj.leader_id.name) ||
                        (obj.leader && obj.leader.name) ||
                        obj.name ||
                        obj.leader_name ||
                        '');

                    // ID matching - try all possible ID fields
                    let leaderId = obj.leader_id;
                    if (typeof leaderId === 'string') {
                        keys.id = normalize(leaderId);
                    } else if (typeof leaderId === 'object' && leaderId) {
                        keys.id = normalize(leaderId._id || leaderId.id || '');
                    }

                    // PIN matching - try all possible PIN/TATA ID fields
                    keys.pin = normalize(
                        (obj.leader_id && (obj.leader_id.pin || obj.leader_id.wealthplus_id || obj.leader_id.tata_id)) ||
                        (obj.leader && (obj.leader.pin || obj.leader.wealthplus_id || obj.leader.tata_id)) ||
                        obj.pin ||
                        obj.wealthplus_id ||
                        obj.tata_id ||
                        obj.tataId ||
                        ''
                    );

                    // Log extraction details


                    return keys;
                };

                const itemKeys = extractLeaderKeys(item);

                const monthKey = (m) => {
                    const rawMonth = (m || '').toString().slice(0, 7);

                    return rawMonth;
                };

                // Enhanced status detection
                const normalizeStatus = s => (s || '').toString().toLowerCase().trim();

                // Get all possible status indicators
                const backendStatus = normalizeStatus(item.status);
                const paymentStatus = normalizeStatus(item.payment_status);
                const rawUiStatus = normalizeStatus(uiStatus);
                const commissionId = item.id || item._id || item.dbId;

                // Debug the commission record


                // Check if there's a matching paid payout
                const matchingPayout = paidPayouts?.find(pp => {
                    const payoutCommissionId = pp.commission_id || pp.id;
                    const payoutMonth = normalizeStatus(pp.month).slice(0, 7);
                    const itemMonth = normalizeStatus(item.month).slice(0, 7);
                    const payoutLeaderId = pp.leader_id?._id || pp.leader_id;
                    const itemLeaderId = item.leaderId || (item.leader_id && item.leader_id._id);

                    const matches = (
                        // Match by commission ID if available
                        (payoutCommissionId && commissionId && payoutCommissionId === commissionId) ||
                        // Or match by leader + month + amount
                        (payoutLeaderId === itemLeaderId &&
                            payoutMonth === itemMonth &&
                            parseFloat(pp.amount || pp.totalCommission || 0) === parseFloat(item.totalCommission || 0))
                    );

                    if (matches) {

                    }
                    return matches;
                });

                // A commission is considered paid if:
                // 1. It has paid status in any form
                // 2. It has matching paid payout record
                // 3. It is explicitly marked as paid
                const isPaidFlag =
                    ['paid', 'completed', 'success', 'successful'].includes(backendStatus) ||
                    ['paid', 'completed', 'success', 'successful'].includes(paymentStatus) ||
                    rawUiStatus === 'paid' ||
                    item.paid === true ||
                    matchingPayout?.status?.toLowerCase() === 'paid';





                // Check paid payouts list for a matching paid record (match by id, pin or name) and ensure payout record indicates a paid-like state where possible
                const matchAttempts = [];
                const isAlreadyPaid = isPaidFlag || (paidPayouts || []).some(pp => {
                    const ppKeys = extractLeaderKeys(pp);
                    const attempt = {
                        ppId: pp._id || pp.id,
                        ppMonth: pp.month,
                        ppKeys,
                        itemKeys,
                        ppRaw: {
                            name: pp.leaderName,
                            id: pp.leader_id?._id,
                            status: pp.status,
                            month: pp.month
                        },
                        itemRaw: {
                            name: item.leaderName,
                            id: item.leader_id?._id,
                            month: item.month
                        }
                    };

                    // First check - commission/payout ID match
                    const normalize = str => (str || '').toString().toLowerCase().trim();
                    const commissionId = normalize(item.commission_id || item.id || item._id);
                    const payoutId = normalize(pp.commission_id || pp._id || pp.id);
                    const idMatch = commissionId && payoutId && commissionId === payoutId;

                    // Second check - leader name/currentUser match
                    const itemName = normalize(item.leaderName || item.currentUserName);
                    const ppName = normalize(pp.leaderName || pp.leader_id?.name || pp.leader?.name);
                    const nameMatch = itemName && ppName && itemName === ppName;

                    // Third check - leader ID match
                    const itemLeaderId = normalize(item.leaderId || (item.leader_id && item.leader_id._id));
                    const ppLeaderId = normalize(pp.leaderId || (pp.leader_id && pp.leader_id._id));
                    const leaderIdMatch = itemLeaderId && ppLeaderId && itemLeaderId === ppLeaderId;

                    // Month matching - try different formats
                    const paidMonth = normalize(pp.month).slice(0, 7);
                    const itemMonth = normalize(item.month).slice(0, 7);
                    const monthMatch = paidMonth && itemMonth && paidMonth === itemMonth;

                    // Debug match criteria


                    attempt.matches = {
                        byId: idMatch,
                        byName: nameMatch,
                        byLeaderId: leaderIdMatch,
                        byMonth: monthMatch
                    };

                    // Match if any identifier matches and month matches
                    const isMatched = (idMatch || nameMatch || leaderIdMatch) && monthMatch;

                    matchAttempts.push(attempt);

                    if (attempt.matched) {

                    }

                    return attempt.matched;
                });

                // Log detailed payment status check


                if (uiStatus === 'paid' || isAlreadyPaid) {
                    // Try to show transaction id if available from either source
                    const paidRecord = (paidPayouts || []).find(pp => {
                        const ppKeys = extractLeaderKeys(pp);
                        const nameMatch = itemKeys.name && ppKeys.name && itemKeys.name === ppKeys.name;
                        const idMatch = itemKeys.id && ppKeys.id && itemKeys.id === ppKeys.id;
                        const pinMatch = (itemKeys.pin && ppKeys.pin && itemKeys.pin === ppKeys.pin) || (itemKeys.pinFallback && ppKeys.pin && itemKeys.pinFallback === ppKeys.pin);
                        const paidMonth = monthKey(pp.month);
                        const itemMonth = monthKey(item.month);
                        return (nameMatch || idMatch || pinMatch) && paidMonth && itemMonth && paidMonth === itemMonth;
                    }) || {};

                    return (
                        <div className="flex flex-col">
                            <span className="text-green-600 text-sm font-medium">✓ Paid</span>
                            {paidRecord.transaction_ref && <span className="text-xs text-gray-500 mt-1">Txn: {paidRecord.transaction_ref}</span>}
                        </div>
                    );
                }

                // For other statuses, show actions (View/Edit/Send/Approve/Process)

                return (
                    <div className="flex space-x-2">
                        <button onClick={() => handleViewCalculation(item.id)} className="px-3 py-1 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-sm">View</button>
                        {editingCommission === item.id ? (
                            <>
                                <button onClick={handleSaveCommission} className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm">Save</button>
                                <button onClick={handleCancelEdit} className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm">Cancel</button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => handleEditCommission(item)} className="px-3 py-1 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 text-sm">Edit</button>
                                {(() => {
                                    // Check if the commission is already paid
                                    const rawStatus = (item.status || '').toString().toLowerCase();

                                    if (rawStatus === 'paid' || uiStatus === 'paid' || item.paid === true) {
                                        return (
                                            <div className="flex flex-col">
                                                <span className="text-green-600 text-sm font-medium">✓ Paid</span>
                                                {(item.transaction_ref || item.transaction_id) &&
                                                    <span className="text-xs text-gray-500 mt-1">
                                                        Txn: {item.transaction_ref || item.transaction_id}
                                                    </span>
                                                }
                                            </div>
                                        );
                                    }

                                    // Show appropriate action button based on status
                                    if ((uiStatus === 'calculated' || uiStatus === 'default') && rawStatus !== 'paid') {
                                        return (
                                            <button
                                                onClick={() => handleApprovePayout(item.dbId || item.id)}
                                                className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm disabled:bg-gray-400"
                                                disabled={recalculating || approvingPayout === (item.dbId || item.id)}
                                            >
                                                Approve & Pay
                                            </button>
                                        );
                                    } else if (uiStatus === 'approved') {
                                        return (
                                            <button
                                                onClick={() => setProcessingPayment(item.dbId || item.id)}
                                                className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm disabled:bg-gray-400"
                                                disabled={recalculating || processingPayment === (item.dbId || item.id)}
                                            >
                                                Process Payment
                                            </button>
                                        );
                                    } else if (uiStatus === 'emailed') {
                                        return <span className="text-blue-600 text-sm font-medium">✓ Emailed</span>;
                                    } else {
                                        return <span className="text-gray-500 text-sm">{item.status || 'N/A'}</span>;
                                    }
                                })()}
                            </>
                        )}
                    </div>
                );
            }
        }
    ];

    const paidPayoutColumns = [
        { header: 'Paid To', accessor: (item) => item.leaderName || item.leader_id?.name || 'Unknown Leader' },
        {
            header: 'Level', accessor: (item) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.hierarchyLevel === 'L1' ? 'bg-blue-100 text-blue-800' :
                    item.hierarchyLevel === 'L2' ? 'bg-green-100 text-green-800' :
                        item.hierarchyLevel === 'L3' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                    }`}>
                    {item.hierarchyLevel || 'L1'}
                </span>
            )
        },
        { header: 'Month', accessor: (item) => item.month },
        { header: 'Commission Amount', accessor: (item) => `₹${(item.totalCommission || item.amount || 0).toLocaleString('en-IN')}` },
        {
            header: 'Transaction ID',
            accessor: (item) => item.transaction_ref ? (
                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{item.transaction_ref}</span>
            ) : (
                <span className="text-gray-400 text-sm">-</span>
            )
        },
        { header: 'Payment Method', accessor: (item) => item.payment_method || item.method || 'N/A' },
        { header: 'Paid Date', accessor: (item) => item.paid_at ? new Date(item.paid_at).toLocaleDateString() : item.approved_at ? new Date(item.approved_at).toLocaleDateString() : 'N/A' },
        { header: 'Approved By', accessor: (item) => item.approved_by?.name || 'N/A' },
        { header: 'Paid By', accessor: (item) => item.paid_by?.name || 'N/A' },
        {
            header: 'Action',
            accessor: (item) => (
                <div className="flex space-x-2">
                    <button onClick={() => handleViewCalculation(item.id)} className="px-3 py-1 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-sm">View</button>
                    <button onClick={() => handleEditPayout(item)} className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm">Edit</button>
                    <button onClick={() => handleDeletePayout(item)} className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm">Delete</button>
                </div>
            )
        }
    ];

    const performerColumns = [
        { header: 'Rank', accessor: (_item, index) => `#${index + 1}` },
        { header: 'Name', accessor: (item) => item.name },
        { header: 'Revenue', accessor: (item) => `₹${item.value.toLocaleString('en-IN')}` },
    ];

    const auditLogColumns = [
        { header: 'Timestamp', accessor: (item) => new Date(item.timestamp).toLocaleString() },
        { header: 'User', accessor: (item) => item.user },
        { header: 'Action', accessor: (item) => <span className="font-mono bg-gray-200 px-2 py-1 rounded text-xs">{item.action}</span> },
        { header: 'Details', accessor: (item) => item.details },
    ];


    // Function to get full hierarchy path
    const getHierarchyPath = (user, allUsers = usersData || []) => {
        // If no users data loaded yet, return loading state
        if (!allUsers || allUsers.length === 0) {
            return user.role === 'super_admin' ? 'Super Admin' : 'Loading...';
        }

        // If no leader_id, show role-based message
        if (!user.leader_id) {
            if (user.role === 'super_admin') return 'Super Admin';
            return 'No Leader Assigned';
        }

        const hierarchy = [];
        let currentUser = user;

        // Traverse up the hierarchy chain
        while (currentUser && currentUser.leader_id) {
            // Handle both populated object and string leader_id
            const leaderId = typeof currentUser.leader_id === 'object' ? currentUser.leader_id._id : currentUser.leader_id;
            const leader = (allUsers || []).find(u => u._id === leaderId);

            if (leader) {
                hierarchy.unshift(leader.name);
                currentUser = leader;
            } else {
                break;
            }
        }

        return hierarchy.length > 0 ? hierarchy.join(' > ') : 'Leader Not Found';
    };

    // Function to get available leaders for selection
    const getAvailableLeaders = (currentUserRole, excludeUserId = null) => {
        if (currentUserRole === 'super_admin') return [];

        return (usersData || [])
            .filter(user =>
                (user.role === 'leader' || user.role === 'super_admin') &&
                user.status === 'active' &&
                user._id !== excludeUserId
            )
            .map(user => ({
                ...user,
                hierarchyPath: getHierarchyPath(user, usersData || [])
            }));
    };

    const userColumns = [
        { header: 'Name', accessor: (item) => item.name },
        { header: 'Email', accessor: (item) => item.email || 'N/A' },
        { header: 'Phone', accessor: (item) => item.phone || 'N/A' },
        { header: 'WealthPlus ID', accessor: (item) => item.wealthplus_id || item.tata_id || 'N/A' },
        { header: 'Role', accessor: (item) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.role === 'super_admin' ? 'bg-red-100 text-red-800' : item.role === 'leader' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{item.role}</span> },
        { header: 'Designation', accessor: (item) => item.designation_title || '-' },
        { header: 'Reports To', accessor: (item) => getHierarchyPath(item, usersData) },
        { header: 'Status', accessor: (item) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{item.status}</span> },
        {
            header: 'Action',
            accessor: (item) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEditUser(item)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleDeleteUser(item)}
                        className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];


    const filteredUsers = (usersData || []).filter(user =>
        (user.name || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.phone || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.role || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.role || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.wealthplus_id || user.tata_id || '').toLowerCase().includes(userSearchTerm.toLowerCase())
    );


    if (loading) {
        return <LoadingSpinner />;
    }


    if (!data) {
        return <div className="text-center text-red-500">Failed to load dashboard data.</div>;
    }

    const payoutTableTitle = recalculationPeriod
        ? `Payout Approvals (Recalculated for ${recalculationPeriod})`
        : "Payout Approvals (Current Month)";

    // Update title to show "Recalculated for 2025-10" when commissions are loaded from localStorage
    const displayTableTitle = recalculationPeriod === '2025-10'
        ? "Payout Approvals (Recalculated for 2025-10)"
        : payoutTableTitle;

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return <DashboardTab data={data} />;
            case 'payouts':
                return (
                    <PayoutsTab
                        data={data}
                        payoutColumns={payoutColumns}
                        payoutTableTitle={displayTableTitle}
                        recalculating={recalculating}
                        fetchData={fetchData}
                        CommissionManager={CommissionManager}
                        onRecalculate={handleRecalculation}
                    />
                );
            case 'reports':
                return (
                    <ReportsTab
                        data={data}
                        performerColumns={performerColumns}
                        onMonthChange={handleReportMonthChange}
                        selectedMonth={selectedReportMonth}
                    />
                );
            case 'users':
                return (
                    <UsersTab
                        usersData={usersData}
                        userSearchTerm={userSearchTerm}
                        setUserSearchTerm={setUserSearchTerm}
                        filteredUsers={filteredUsers}
                        userColumns={userColumns}
                        showUserForm={showUserForm}
                        setShowUserForm={setShowUserForm}
                        newUser={newUser}
                        setNewUser={setNewUser}
                        handleCreateUser={handleCreateUser}
                        showEditForm={showEditForm}
                        setShowEditForm={setShowEditForm}
                        editUser={editUser}
                        setEditUser={setEditUser}
                        handleUpdateUser={handleUpdateUser}
                        handleEditUser={handleEditUser}
                        handleDeleteUser={handleDeleteUser}
                        getAvailableLeaders={getAvailableLeaders}
                        // Excel upload now handled in PolicyDataManager
                        showManualPolicyForm={showManualPolicyForm}
                        setShowManualPolicyForm={setShowManualPolicyForm}
                        newPolicy={newPolicy}
                        setNewPolicy={setNewPolicy}
                        handleManualPolicySubmit={handleManualPolicySubmit}
                        creatingUser={creatingUser}
                    />
                );
            case 'policies':

                return (
                    <PolicyDataManager
                        user={user}
                        usersData={usersData || []}
                        onPolicyUpdate={() => {
                            // Refresh data if needed
                            fetchData();
                            // fetchPeopleData is not defined in this scope/component, removing it to fix crash
                            // fetchPeopleData();
                        }}
                        onRecalculate={handleRecalculation}
                        excelUploadErrors={excelUploadErrors}
                        setExcelUploadErrors={setExcelUploadErrors}
                        showExcelErrorsModal={showExcelErrorsModal}
                        setShowExcelErrorsModal={setShowExcelErrorsModal}
                    />
                );
            case 'paid-payouts':
                return (
                    <PaidPayoutsTab
                        paidPayouts={paidPayouts}
                        paidPayoutColumns={paidPayoutColumns}
                        loading={paidPayoutsLoading}
                        selectedMonth={selectedPaidMonth}
                        setSelectedMonth={setSelectedPaidMonth}
                        selectedYear={selectedPaidYear}
                        setSelectedYear={setSelectedPaidYear}
                        fetchPaidPayouts={fetchPaidPayouts}
                    />
                );
            case 'audit':

                return <AuditTab data={data} auditLogColumns={auditLogColumns} />;
            case 'general-insurance':
                return <GeneralInsuranceTab userRole="super_admin" user={user} />;
            case 'health-insurance':
                return <HealthInsuranceWrapper />;
            case 'mutual-fund':
                return <MutualFundTab userRole="super_admin" userId={user.id} />;

            case 'posters':
                return (
                    <PosterManagementTab
                        posters={posters}
                        posterCategories={posterCategories}
                        loading={posterLoading}
                        uploading={posterUploading}
                        selectedCategory={selectedPosterCategory}
                        setSelectedCategory={setSelectedPosterCategory}

                        viewMode={posterViewMode}
                        setViewMode={setPosterViewMode}
                        showUploadModal={showPosterUploadModal}
                        setShowUploadModal={setShowPosterUploadModal}
                        showEditModal={showPosterEditModal}
                        setShowEditModal={setShowPosterEditModal}
                        showCategoryModal={showPosterCategoryModal}
                        setShowCategoryModal={setShowPosterCategoryModal}
                        editingPoster={editingPoster}
                        setEditingPoster={setEditingPoster}
                        uploadForm={posterUploadForm}
                        setUploadForm={setPosterUploadForm}
                        editForm={posterEditForm}
                        setEditForm={setPosterEditForm}
                        categoryForm={posterCategoryForm}
                        setCategoryForm={setPosterCategoryForm}
                        handleUpload={handlePosterUpload}
                        handleEdit={handlePosterEdit}
                        handleDelete={handlePosterDelete}
                        handleCreateCategory={handleCreateCategory}
                        handleUpdateCategory={handleUpdateCategory}
                        handleDeleteCategory={handleDeleteCategory}
                        fetchPosters={fetchPosters}
                        fetchCategories={fetchPosters}
                    />
                );
            default:
                return null;
        }
    }

    return (
        <div className="space-y-8">
            <h2 className="text-3xl font-bold text-gray-900">Welcome, {user.name}</h2>

            <div className="flex space-x-4">
                {/* Excel upload is now handled in PolicyDataManager component */}
            </div>

            <nav className="bg-white p-2 rounded-lg shadow-md flex space-x-2 overflow-x-auto">
                <NavItem
                    label="TATA AIG"
                    isActive={['dashboard', 'users', 'policies', 'payouts', 'paid-payouts'].includes(activeTab)}
                    onClick={() => { setActiveTab('dashboard'); localStorage.setItem('superAdminActiveTab', 'dashboard'); }}
                />
                <NavItem label="Health Insurance" isActive={activeTab === 'health-insurance'} onClick={() => { setActiveTab('health-insurance'); localStorage.setItem('superAdminActiveTab', 'health-insurance'); }} />
                <NavItem label="General Insurance" isActive={activeTab === 'general-insurance'} onClick={() => { setActiveTab('general-insurance'); localStorage.setItem('superAdminActiveTab', 'general-insurance'); }} />
                <NavItem label="Mutual Funds" isActive={activeTab === 'mutual-fund'} onClick={() => { setActiveTab('mutual-fund'); localStorage.setItem('superAdminActiveTab', 'mutual-fund'); }} />
                <NavItem label="Poster Management" isActive={activeTab === 'posters'} onClick={() => { setActiveTab('posters'); localStorage.setItem('superAdminActiveTab', 'posters'); }} />
                <NavItem label="Performance Reports" isActive={activeTab === 'reports'} onClick={() => { setActiveTab('reports'); localStorage.setItem('superAdminActiveTab', 'reports'); }} />
                <NavItem label="Audit Logs" isActive={activeTab === 'audit'} onClick={() => { setActiveTab('audit'); localStorage.setItem('superAdminActiveTab', 'audit'); }} />
            </nav >

            {/* Sub-navigation for TATA AIG */}
            {['dashboard', 'users', 'policies', 'payouts', 'paid-payouts'].includes(activeTab) && (
                <div className="bg-gray-50 p-2 rounded-lg flex space-x-2 overflow-x-auto border border-gray-100">
                    <button
                        onClick={() => { setActiveTab('dashboard'); localStorage.setItem('superAdminActiveTab', 'dashboard'); }}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'dashboard' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Dashboard
                    </button>
                    <button
                        onClick={() => { setActiveTab('users'); localStorage.setItem('superAdminActiveTab', 'users'); }}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'users' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Users
                    </button>
                    <button
                        onClick={() => { setActiveTab('policies'); localStorage.setItem('superAdminActiveTab', 'policies'); }}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'policies' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Policy Data
                    </button>
                    <button
                        onClick={() => { setActiveTab('payouts'); localStorage.setItem('superAdminActiveTab', 'payouts'); }}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'payouts' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Payouts & Commissions
                    </button>
                    <button
                        onClick={() => { setActiveTab('paid-payouts'); localStorage.setItem('superAdminActiveTab', 'paid-payouts'); }}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'paid-payouts' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Paid Payouts
                    </button>
                </div>
            )}

            <div className="mt-6">
                {renderContent()}
            </div>

            <CalculationModal
                showCalculationModal={showCalculationModal}
                setShowCalculationModal={setShowCalculationModal}
                calculationData={calculationData}
            />

            <PaymentModal
                processingPayment={processingPayment}
                setProcessingPayment={setProcessingPayment}
                transactionData={transactionData}
                setTransactionData={setTransactionData}
                handlePaymentInputChange={handlePaymentInputChange}
                handleProcessPayment={handleProcessPayment}
            />

            <ApprovalModal
                approvingPayout={approvingPayout}
                setApprovingPayout={setApprovingPayout}
                transactionData={transactionData}
                setTransactionData={setTransactionData}
                handlePaymentInputChange={handlePaymentInputChange}
                handleConfirmApproval={handleConfirmApproval}
            />


            {/* Excel Upload Errors Modal */}
            {
                showExcelErrorsModal && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
                            <div className="mt-3">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Excel Upload Errors</h3>
                                    <button
                                        onClick={() => setShowExcelErrorsModal(false)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <span className="text-2xl">&times;</span>
                                    </button>
                                </div>

                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-sm text-red-800">
                                        <strong>{excelUploadErrors.length} errors found</strong> during Excel upload.
                                        Please fix these issues and try again.
                                    </p>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cell</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {excelUploadErrors.map((error, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        Row {error.row}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-mono">
                                                        {error.cell}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono bg-gray-50">
                                                        {String(error.value || 'N/A').substring(0, 50)}{String(error.value || '').length > 50 ? '...' : ''}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-red-700">
                                                        {error.error}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="flex justify-end mt-6 space-x-3">
                                    <button
                                        onClick={() => setShowExcelErrorsModal(false)}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                    >
                                        Close
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowExcelErrorsModal(false);
                                            setExcelUploadErrors([]);
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

// Icon Components
const UploadIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>;
const PlusCircleIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" className={className || "h-6 w-6"} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

export default SuperAdminDashboard;
